import { ERROR_MESSAGE_CONTACT, REFRESH_TOKEN_URL } from '../shared/constants';
import { getEnvironment } from '../env/env';
import { getAccessToken } from 'src/utils/sse.utils';

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const fetchRequest = async (
  method: HttpMethod,
  url: string,
  data?: Record<string, unknown> | null,
  headers?: Record<string, string>,
  mode?: RequestMode,
): Promise<any> => {
  const mergedHeaders: Record<string, string> = {
    Accept: 'application/json, application/xml, text/plain, text/html, *.*',
    'Content-Type': 'application/json; charset=utf-8',
    ...headers,
  };

  let isError = false;

  const params: RequestInit = {
    method,
    headers: mergedHeaders,
  };

  if (data) {
    params.body = JSON.stringify(data);
  }

  if (mode) {
    params.mode = mode;
  }

  return fetch(url, params)
    .then((response) => {
      isError = !response.ok;
      if (!response.ok) {
        return response.json();
      } else {
        if (response.statusText === 'No Content') {
          return response.ok;
        } else {
          return response.json();
        }
      }
    })
    .then((response) => {
      if (!isError) {
        return response;
      } else {
        let errorMessage = '';
        JSON.stringify(response, (_key: string, value: unknown) => {
          if (typeof value === 'string') {
            errorMessage += errorMessage ? `, ${value}` : value;
          }
          return value;
        });
        return new Error(errorMessage);
      }
    })
    .catch((error) => {
      console.error(error);
      return new Error(ERROR_MESSAGE_CONTACT);
    });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const fetchRequestWithAccessToken = async (
  method: HttpMethod,
  url: string,
  data?: Record<string, unknown> | null,
  headers?: Record<string, string>,
): Promise<any> => {
  const access = getAccessToken();

  const mergedHeaders: Record<string, string> = {
    Accept: 'application/json, application/xml, text/plain, text/html, *.*',
    'Content-Type': 'application/json; charset=utf-8',
    Authorization: `Bearer ${access}`,
    ...headers,
  };

  let isError = false;

  const params: RequestInit = {
    method,
    headers: mergedHeaders,
  };

  if (data) {
    params.body = JSON.stringify(data);
  }

  const response = await fetch(url, params).catch((error) => {
    console.error(error);
    return new Error(ERROR_MESSAGE_CONTACT);
  });

  if (response instanceof Error) {
    return response;
  }

  isError = !response.ok;

  if ([403, 422].includes(response.status)) {
    const raw = localStorage.getItem('user');
    const user = raw ? JSON.parse(raw) : {};
    const token = await refreshToken(user.refresh_token);

    if (token.access_token) {
      const newUser = {
        ...user,
        access_token: token.access_token,
      };

      localStorage.setItem('user', JSON.stringify(newUser));
    } else {
      return token;
    }
  }

  if (isError) {
    let errorMessage = '';
    const json = await response.json();
    JSON.stringify(json, (_key: string, value: unknown) => {
      if (typeof value === 'string') {
        errorMessage += errorMessage ? `, ${value}` : value;
      }
      return value;
    });

    if (response.status === 401) {
      localStorage.removeItem('user');
      window.location.assign('/auth');
      return;
    }

    return new Error(errorMessage);
  } else {
    if (response.statusText === 'No Content') {
      return response.ok;
    } else {
      return response.json();
    }
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const uploadFileWithAccessToken = async (
  method: HttpMethod,
  url: string,
  data?: FormData | null,
  headers?: Record<string, string>,
): Promise<any> => {
  const access = getAccessToken();

  const mergedHeaders: Record<string, string> = {
    Accept: 'application/json',
    Authorization: `Bearer ${access}`,
    ...headers,
  };

  let isError = false;

  const params: RequestInit = {
    method,
    headers: mergedHeaders,
  };

  if (data) {
    params.body = data;
  }

  const response = await fetch(url, params).catch((error) => {
    console.error(error);
    return new Error(ERROR_MESSAGE_CONTACT);
  });

  if (response instanceof Error) {
    return response;
  }

  isError = !response.ok;

  if ([403, 422].includes(response.status)) {
    const raw = localStorage.getItem('user');
    const user = raw ? JSON.parse(raw) : {};
    const token = await refreshToken(user.refresh_token);

    if (token.access_token) {
      const newUser = {
        ...user,
        access_token: token.access_token,
      };

      localStorage.setItem('user', JSON.stringify(newUser));
    } else {
      return token;
    }
  }

  if (isError) {
    let errorMessage = '';
    const json = await response.json();
    JSON.stringify(json, (_key: string, value: unknown) => {
      if (typeof value === 'string') {
        errorMessage += errorMessage ? `, ${value}` : value;
      }
      return value;
    });
    return new Error(errorMessage);
  } else {
    if (response.statusText === 'No Content') {
      return response.ok;
    } else {
      return response.json();
    }
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const refreshToken = async (token: string | null): Promise<any> => {
  const environment = await getEnvironment();
  const url = `${environment.APP_SSO_BASE_URL}/${REFRESH_TOKEN_URL}`;

  return fetchRequest('POST', url, { refresh_token: token });
};
